import {Checkbox, Select, Spin, Tag} from "antd";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {convertToArray, isNullOrUndefined, isNullOrUndefinedOrEmpty} from "../../common/utils";
import { KeyConditionActions } from "../../pages/userGroups/KeyCondition/model";
import { useGetInputOptions } from "../condition/inputFields/hooks/GetInputOptions";
import { useLoadingInputOptions } from "../condition/inputFields/hooks/LoadingInputOptions";
import { InputFields } from "../condition/inputFields/userGroupInputFields";
import { createConditionTree } from "../condition/utils/createConditionTree";
import { getInputValuesFromGivenCondition } from "../condition/utils/getInputValuesFromGivenCondition";
import { isNullCondition } from "../condition/utils/isNullCondition";
import "./index.scss";
import { inputFieldDataSync } from "./inputFieldDataSync";
import { keyConditionTransitionsBasedOnInputs } from "./keyConditionTransitionsBasedOnInputs";
import SelectedUserInput from "./SelectedUserInput";
import {CloseOutlined} from "@ant-design/icons";
import { NullCondition } from "../condition/model/SimpleCondition";
import { Operators } from "../condition/model/Operators";

function IndividualInput({attribute, selectedValue, inputChange}) {
  let finalOptions = [];
  const selectedValueInArrayFormat = isNullOrUndefined(selectedValue) ? [] : convertToArray(selectedValue)
  const {Option} = Select;

  function sortSelectedOptionsToTopOfDropdownOptions() {
    return finalOptions.sort((option1, _option2) => {
      return !selectedValueInArrayFormat.includes(option1.value) ? 1 : -1;
    })
  }

  function isOptionSelected(value) {
    return selectedValueInArrayFormat.includes(value);
  }

  function activeClassName() {
    if (!isNullOrUndefinedOrEmpty(selectedValueInArrayFormat)) {
      return "selected-input";
    }
  }

  attribute.options?.forEach((option, index) => {
    finalOptions.push({
      key: index,
      label: option,
      value: option,
    });
  });

  finalOptions = sortSelectedOptionsToTopOfDropdownOptions()

  return (
      <div className="keycondition-individual-input">
        <div className="keycondition-individual-input-label">{attribute.label}</div>
        <div className={activeClassName()}>
          <Select
              mode="multiple"
              placeholder="Please select"
              onChange={(value) => {
                inputChange(attribute.attributeName, value);
              }}
              value={selectedValueInArrayFormat}
              maxTagCount={1}
              dropdownRender={(options) => {
                return <div className={"key-condition-attribute-options"}>{options}</div>
              }}
              menuItemSelectedIcon={({_isSelected}) => {
                return null
              }}
              tagRender={({value, onClose}) => {
                return (
                  <Tag style={{display:"flex", alignItems: "center"}}>
                    <div className={"multi-select-tag"}>{value}</div>
                    <CloseOutlined onClick={onClose}/>
                  </Tag>
                )
              }}
              showArrow={true}
              popupMatchSelectWidth={true}
              style={{minWidth: "150px", maxWidth: "225px"}}
          >
            {finalOptions.map((finalOption) => {
              return (
                  <Option key={finalOption.key} value={finalOption.value}>
                    <Checkbox checked={isOptionSelected(finalOption.value)}/>
                    <label title={finalOption.value} style={{cursor: "pointer"}}>{finalOption.value}</label>
                  </Option>)
            })}
          </Select>
        </div>
      </div>
  );
}

function KeyConditionInputs({ isIndividualInclusionMember }) {
  const { isInProgress, isInError } = useLoadingInputOptions(false);
  const inputFieldOptions = useGetInputOptions();
  const dispatch = useDispatch();
  const editedMembershipCondition = useSelector((rootState) => {
    return rootState.keyCondition.modifiedKeyCondition;
  });
  const [keyCondition, setkeyCondition] = useState(null);
  const initialInputSelectedState = {};
  const [selectedInputState, setSelectedInputState] = useState({
    ...initialInputSelectedState,
  });

  const selectedUser = useSelector((rootState) => rootState.keyCondition.selectedUser);

  function extractingOptionsForTheInputField(attribute) {
    let options = [];
    const attributeMainKey = InputFields[attribute]?.inputOptionsAttribute?.mainAttribute;
    const attributeKey = InputFields[attribute]?.inputOptionsAttribute?.key;
    if (isNullOrUndefined(attributeKey)) {
      options = inputFieldOptions[attributeMainKey];
    } else {
      inputFieldOptions[attributeMainKey]?.forEach((singleOption) => {
        if (Array.isArray(singleOption[attributeKey])) {
          singleOption[attributeKey].forEach((value) => {
            options.push(value);
          });
        } else {
          options.push(singleOption[attributeKey]);
        }
      });
    }

    options = [...new Set(options)];
    return options;
  }

  useEffect(() => {
    let keyConditionCopy = keyCondition;
    if (!isNullCondition(keyConditionCopy)) {
      keyConditionCopy = keyConditionCopy.toJSON(true);
    }
    dispatch(KeyConditionActions.setModifiedKeyCondition(keyConditionCopy));
  }, [JSON.stringify(selectedInputState)]);

  useEffect(() => {
    if (!isNullOrUndefined(editedMembershipCondition)) {
      const editedMembershipConditionTree = createConditionTree(editedMembershipCondition, null);
      setkeyCondition(editedMembershipConditionTree);
      const inputFieldsAndCorrespondingValuesPresentInEditedCondition =
        getInputValuesFromGivenCondition(editedMembershipConditionTree);
      setSelectedInputState({
        ...selectedInputState,
        ...inputFieldsAndCorrespondingValuesPresentInEditedCondition,
      });
    } else {
      setSelectedInputState(initialInputSelectedState)
      setkeyCondition(
        new NullCondition({
          id: "1",
          pathFromRoot: ["1"],
          operator: Operators.noConditionNull,
          isKeyCondition: false,
        }, null)
      );
    }
  }, [JSON.stringify(editedMembershipCondition)]);

  let attributes = [];

  function extractingInputFieldsAttributes() {
    if (isInProgress === false) {
      for (let attribute in InputFields) {
        if (InputFields[attribute].isEnabled === true) {
          const label = InputFields[attribute].attributeLabel;
          const attributeName = InputFields[attribute].attributeName;
          if (isNullOrUndefined(selectedUser)) {
            let options = extractingOptionsForTheInputField(attribute);
            attributes.push({ label, attributeName, options });
            continue;
          }

          attributes.push({ label, attributeName });
        }
      }
    }
  }

  function inputChange(changedAttributeName, changedValues) {
    let keyConditionCopy = keyCondition;
    let relatedAttributeName = InputFields[changedAttributeName].relatedTo;
    let relatedValues;
    if (!isNullOrUndefined(relatedAttributeName)) {
      relatedValues = selectedInputState[relatedAttributeName];
    }
    changedValues = {
      [changedAttributeName]: changedValues,
    };
    keyConditionCopy = keyConditionTransitionsBasedOnInputs(
      keyConditionCopy,
      changedAttributeName,
      changedValues[changedAttributeName],
    );
    let newRelatedValues = {};
    newRelatedValues[relatedAttributeName] = [];
    changedValues[changedAttributeName].forEach(value => {
      const values = inputFieldDataSync(
        changedAttributeName,
        value,
        relatedValues,
        relatedAttributeName,
        inputFieldOptions,
      );
      values.forEach(value => {
        if (!newRelatedValues[relatedAttributeName].includes(value))
          newRelatedValues[relatedAttributeName].push(value);
      });
    });
    const isRelatedValueChanged = relatedValues !== newRelatedValues[relatedAttributeName];
    const toTriggerKeyConditionChangeForRelatedValueChange =
      !isNullOrUndefined(relatedAttributeName) &&
      isRelatedValueChanged;
    if (toTriggerKeyConditionChangeForRelatedValueChange) {
      keyConditionCopy = keyConditionTransitionsBasedOnInputs(
        keyConditionCopy,
        relatedAttributeName,
        newRelatedValues[relatedAttributeName],
      );
    }
    setkeyCondition(keyConditionCopy);
    setSelectedInputState({ ...selectedInputState, ...newRelatedValues, ...changedValues });
  }

  extractingInputFieldsAttributes();

  if (!isNullOrUndefined(selectedUser)) {
    return (
      <div className="keycondition-inputs">
        {attributes.map((attribute, index) => {
          if(InputFields[attribute.attributeName].isDisabledForSelectByName === false 
            || isNullOrUndefined(InputFields[attribute.attributeName].isDisabledForSelectByName)) {
          return (
            <Fragment key={index}>
              <SelectedUserInput
                attribute={attribute}
                value={selectedUser[InputFields[attribute.attributeName].userAttribute]}
                isIndividualInclusionMember={isIndividualInclusionMember}
              />
            </Fragment>
          );
          }
          return (<></>)
        })}
      </div>
    );
  } else {
    return (
      <Spin spinning={isInProgress}>
        <div className="keycondition-inputs">
          {isInError === true ? (
            <div className="error-text">Error Fetching Data</div>
          ) : (
            attributes.map((attribute, index) => {
              return (
                <Fragment key={index}>
                  <IndividualInput
                    attribute={attribute}
                    selectedValue={selectedInputState[attribute.attributeName]}
                    inputChange={inputChange}
                  />
                </Fragment>
              );
            })
          )}
        </div>
      </Spin>
    );
  }
}

export default KeyConditionInputs;
